/*
 * Passport / document photo — core engine (engine:browser).
 *
 * EXIF-normalize → find the face → geometry → crown estimate → similarity
 * transform (scale + de-tilt about the eye-line) → exact-px render → optional
 * background / greyscale / corner → size-band JPEG. One parameterized path;
 * every document is just a record in config/passport-photo-spec.php.
 *
 * ── Why the detector is not ours (changed 2026-09-08) ────────────────────────
 * This file used to create its own MediaPipe FaceLandmarker from two URLs (a
 * jsdelivr proxy and jsdelivr itself) and detect once, on the whole frame. Both
 * halves of that were wrong, and production said so: over 90 days 487 of 3 291
 * uploads — 14.8%, roughly one in seven people who got as far as choosing a
 * file — ended on "Лицо не распознано".
 *
 *   · ONE FULL-FRAME PROBE. MediaPipe resizes its input to a fixed size, so
 *     what decides detection is the FRACTION of the frame the face occupies,
 *     not the megapixels. A face at 6% of the width is invisible to it at any
 *     resolution, and "upload a bigger photo" cannot help. Windows can:
 *     FRFind.search re-asks inside rotations and 1/2- and 1/3-frame tiles.
 *   · TWO CDN URLS AND NO LOCAL COPY. face_retouch_sources() puts OUR OWN
 *     origin first, which for Russian traffic is the difference between a tool
 *     that works and a tool that spins. It also owns the CPU-first delegate
 *     ladder and the real-inference probe that a crippled WebGL2 needs.
 *
 * Both already existed as shared primitives; this tool simply was not using
 * them. A found-sideways photo now also straightens itself, because the roll
 * the de-tilt already corrects is measured from the eye line whatever the
 * window that found it.
 *
 * Landmark indices (MediaPipe FaceLandmarker, 478-pt refine mesh):
 *   chin 152 · left iris 468 · right iris 473 · face-oval top 10 · cheeks 234/454
 *   lids 159/145 + 386/374, eye corners 33/133 + 362/263, inner lips 13/14, mouth 78/308
 * Crown is UNMEASURABLE (the mesh tops out at the forehead): estimated above
 * oval-top and user-correctable by dragging the crown handle.
 *
 * No 'unsafe-eval' — tasks-vision runs under 'wasm-unsafe-eval'.
 */
(function () {
    'use strict';

    var IDX = {
        chin: 152, irisL: 468, irisR: 473, ovalTop: 10, cheekL: 234, cheekR: 454,
        lidUpL: 159, lidLoL: 145, eyeInL: 133, eyeOutL: 33,
        lidUpR: 386, lidLoR: 374, eyeInR: 362, eyeOutR: 263,
        lipUp: 13, lipLo: 14, mouthL: 78, mouthR: 308,
    };
    var CROWN_K_DEFAULT = 0.12;

    /* Set by the blade: MediaPipe source ladder (face_retouch_sources) and the
     * matting model + runtime URLs. Kept in one place so a missing wire-up
     * fails loudly here rather than as a mystery deep inside a lane. */
    var CFG = { sources: null, matting: null };

    function configure(cfg) {
        cfg = cfg || {};
        if (cfg.sources) CFG.sources = cfg.sources;
        if (cfg.matting) CFG.matting = cfg.matting;
    }

    function needLandmarks() {
        if (!window.FRLandmarks || !window.FRFind) throw new Error('face-primitives-missing');
        if (!CFG.sources) throw new Error('face-sources-missing');
    }

    async function init(onProgress) {
        needLandmarks();
        if (onProgress && window.FRLandmarks.onProgress) window.FRLandmarks.onProgress(onProgress);
        await window.FRLandmarks.ensure(CFG.sources, { numFaces: 4, mode: 'IMAGE' });
        return true;
    }

    /** Start the download early, without making anyone wait for it. */
    function warm() {
        try { needLandmarks(); window.FRLandmarks.warm(CFG.sources, { numFaces: 4, mode: 'IMAGE' }); }
        catch (_) { }
    }

    // ── EXIF-normalize any source to an upright canvas BEFORE detection ──
    async function toUprightCanvas(source) {
        if (source && source.getContext) return source; // already a canvas
        // Safari до 17, Chrome и Firefox до 111 значения 'from-image' не знают и
        // бросают TypeError — фото не открывалось. Их умолчание и было «как в
        // EXIF» (замер: EXIF-6 без опции даёт 1044×1400 на Chromium 110,
        // Firefox 108 и WebKit 16.4), поэтому повтор без опции ничего не теряет.
        var bmp;
        if (source instanceof Blob) {
            try { bmp = await createImageBitmap(source, { imageOrientation: 'from-image' }); }
            catch (e) { bmp = await createImageBitmap(source); }
        } else {
            bmp = await createImageBitmap(source);
        }
        var cv = document.createElement('canvas');
        cv.width = bmp.width; cv.height = bmp.height;
        cv.getContext('2d').drawImage(bmp, 0, 0);
        if (bmp.close) bmp.close();
        return cv;
    }

    function P(pts, i, W, H) { return { x: pts[i].x * W, y: pts[i].y * H }; }
    function dist(a, b) { return Math.hypot(a.x - b.x, a.y - b.y); }

    function geometryFrom(pts, W, H) {
        var chin = P(pts, IDX.chin, W, H), irisL = P(pts, IDX.irisL, W, H), irisR = P(pts, IDX.irisR, W, H),
            ovalTop = P(pts, IDX.ovalTop, W, H), cheekL = P(pts, IDX.cheekL, W, H), cheekR = P(pts, IDX.cheekR, W, H);
        var eyeX = (irisL.x + irisR.x) / 2, eyeY = (irisL.y + irisR.y) / 2;
        var rollDeg = Math.atan2(irisR.y - irisL.y, irisR.x - irisL.x) * 180 / Math.PI;
        return {
            chin: chin, irisL: irisL, irisR: irisR, ovalTop: ovalTop,
            cheekL: cheekL, cheekR: cheekR,
            eyeX: eyeX, eyeY: eyeY, rollDeg: rollDeg,
            faceWidth: Math.abs(cheekR.x - cheekL.x),
            faceH: chin.y - ovalTop.y               // chin → forehead/mesh top
        };
    }

    /**
     * Eye and mouth openness straight from the mesh.
     *
     * The old code read MediaPipe blendshapes (eyeBlink*, jawOpen), which meant
     * the landmarker had to be created with outputFaceBlendshapes:true — an
     * option the SHARED detector does not set, and should not have to set for
     * one consumer. Aspect ratios need no option and no second model: the lid
     * gap over the eye width, and the inner-lip gap over the mouth width, both
     * scale-free and rotation-free.
     */
    function openness(pts, W, H) {
        function ear(up, lo, inn, out) {
            var w = dist(P(pts, inn, W, H), P(pts, out, W, H));
            return w > 0 ? dist(P(pts, up, W, H), P(pts, lo, W, H)) / w : 0;
        }
        var l = ear(IDX.lidUpL, IDX.lidLoL, IDX.eyeInL, IDX.eyeOutL);
        var r = ear(IDX.lidUpR, IDX.lidLoR, IDX.eyeInR, IDX.eyeOutR);
        var mw = dist(P(pts, IDX.mouthL, W, H), P(pts, IDX.mouthR, W, H));
        var mar = mw > 0 ? dist(P(pts, IDX.lipUp, W, H), P(pts, IDX.lipLo, W, H)) / mw : 0;
        return { ear: Math.min(l, r), earL: l, earR: r, mar: mar };
    }

    function estimateCrownY(geom, k) {
        return geom.ovalTop.y - (k == null ? CROWN_K_DEFAULT : k) * geom.faceH;
    }

    /**
     * Find the face and read its geometry.
     *
     * `deep:false` is the fast path used while re-analysing something we
     * already know contains a face (the rendered result, for the self-check).
     * The upload path runs the full ladder, because that is the whole point.
     */
    async function analyze(source, opts) {
        opts = opts || {};
        await init(opts.onModel);
        var canvas = await toUprightCanvas(source);
        var W = canvas.width, H = canvas.height;
        var found = await window.FRFind.search(canvas, W, H, {
            deep: opts.deep !== false,
            onStep: opts.onStep || null,
            signal: opts.signal || null,
        });
        var faces = found.faces || [];
        var out = {
            ok: faces.length === 1, faces: faces.length, W: W, H: H, canvas: canvas,
            via: found.via, tries: found.tries,
        };
        if (!faces.length) return out;
        // More than one face is still a refusal for a document photo, but the
        // geometry of the largest is what the UI needs in order to SHOW which
        // one it means rather than only saying "too many".
        var pick = faces.slice().sort(function (a, b) { return (b.box.w * b.box.h) - (a.box.w * a.box.h); })[0];
        out.boxes = faces.map(function (f) { return f.box; });
        out.geom = geometryFrom(pick.pts, W, H);
        out.crownY = estimateCrownY(out.geom, null);
        out.openness = openness(pick.pts, W, H);
        return out;
    }

    /** Last resort: the person points at the face the ladder could not find. */
    async function analyzeAt(canvas, nx, ny) {
        needLandmarks();
        var W = canvas.width, H = canvas.height;
        var found = await window.FRFind.around(canvas, W, H, nx, ny, {});
        var faces = found.faces || [];
        var out = { ok: faces.length >= 1, faces: faces.length, W: W, H: H, canvas: canvas, via: found.via };
        if (!faces.length) return out;
        out.geom = geometryFrom(faces[0].pts, W, H);
        out.crownY = estimateCrownY(out.geom, null);
        out.openness = openness(faces[0].pts, W, H);
        out.boxes = [faces[0].box];
        return out;
    }

    function targetPx(spec) {
        if (spec.target_px_w && spec.target_px_h) return { w: spec.target_px_w, h: spec.target_px_h };
        var dpi = spec.dpi || 300;
        return { w: Math.round(spec.print_w_mm / 25.4 * dpi), h: Math.round(spec.print_h_mm / 25.4 * dpi) };
    }

    function headFraction(spec) {
        if (spec.head_mode === 'percent')
            return (spec.head_pct_min + spec.head_pct_max) / 200; // mid, as fraction
        // ru_inverted / mm — head height as fraction of the print height
        var mmMid = (spec.head_mm_min + spec.head_mm_max) / 2;
        return mmMid / spec.print_h_mm;
    }

    /**
     * Where the crown sits, as a fraction of the frame height, for the layout
     * this spec describes. Shared by the transform and by the example
     * schematic, so the picture and the result cannot drift apart.
     */
    function crownFraction(spec) {
        if (spec.head_mode === 'ru_inverted') return spec.top_margin_mm / spec.print_h_mm;
        var eye = (spec.eye_from_top != null) ? spec.eye_from_top : 0.40;
        return eye - 0.46 * headFraction(spec);   // eye sits ≈46% down the head
    }

    /*
     * Similarity transform: maps the source so the head lands at the spec's head
     * size and the eyes sit at the spec's vertical anchor, de-tilted (eyes level)
     * and centred horizontally on the eye midpoint. Returns the canvas matrix
     * [a,b,c,d,e,f] (x' = a·x + c·y + e ; y' = b·x + d·y + f).
     */
    function computeTransform(geom, crownY, spec, opts) {
        opts = opts || {};
        var T = targetPx(spec), TW = T.w, TH = T.h;
        var headH_src = geom.chin.y - crownY;          // chin → estimated crown, source px
        var zoom = opts.zoom || 1;
        var s = (headFraction(spec) * TH) / headH_src * zoom; // uniform scale (× user zoom)

        var anchorX = TW / 2 + (opts.panX || 0), anchorY;     // + user pan X (output px)
        if (spec.head_mode === 'ru_inverted') {
            // pin crown at top_margin_mm; eyes follow from their offset below crown
            var targetCrownY = crownFraction(spec) * TH;
            anchorY = targetCrownY + s * (geom.eyeY - crownY);
        } else {
            var eyeFromTop = (spec.eye_from_top != null) ? spec.eye_from_top : 0.40;
            anchorY = eyeFromTop * TH;
        }
        anchorY += (opts.panY || 0);                          // + user pan Y (output px)

        var baseRoll = (opts.deTilt === false) ? 0 : (geom.rollDeg * Math.PI / 180);
        var roll = baseRoll - ((opts.rotateExtra || 0) * Math.PI / 180); // + manual rotate (deg)
        var cos = Math.cos(-roll), sin = Math.sin(-roll);
        var a = s * cos, b = s * sin, c = -s * sin, d = s * cos;
        var e = anchorX - (a * geom.eyeX + c * geom.eyeY);
        var f = anchorY - (b * geom.eyeX + d * geom.eyeY);
        return { TW: TW, TH: TH, s: s, roll: roll, anchorX: anchorX, anchorY: anchorY,
                 headFrac: headFraction(spec) * zoom, matrix: [a, b, c, d, e, f] };
    }

    /**
     * Render the crop. `k` renders the SAME framing at k× the output size —
     * the matting pass wants more pixels than the print does, and a matte
     * computed on a 354×472 thumbnail is a matte with 354 px of hair in it.
     * Everything downstream is scale-agnostic because the corner and the final
     * downsample happen after.
     */
    function render(srcCanvas, tr, spec, k) {
        k = k || 1;
        var cv = document.createElement('canvas');
        cv.width = Math.round(tr.TW * k); cv.height = Math.round(tr.TH * k);
        var ctx = cv.getContext('2d');
        ctx.fillStyle = spec.bg_hex || '#ffffff';   // spec colour shows wherever source doesn't cover
        ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.imageSmoothingEnabled = true; ctx.imageSmoothingQuality = 'high';
        var m = tr.matrix;
        ctx.setTransform(m[0] * k, m[1] * k, m[2] * k, m[3] * k, m[4] * k, m[5] * k);
        ctx.drawImage(srcCanvas, 0, 0);
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        return cv;
    }

    /** How much extra resolution the matte may have without inventing pixels. */
    function mattingScale(tr) {
        var want = 1400 / Math.max(tr.TW, tr.TH);
        var have = tr.s > 0 ? 1 / tr.s : 1;     // beyond this we would upscale the source
        return Math.max(1, Math.min(want, have, 3));
    }

    function resample(canvas, w, h) {
        if (canvas.width === w && canvas.height === h) return canvas;
        var out = document.createElement('canvas'); out.width = w; out.height = h;
        var c = out.getContext('2d');
        c.imageSmoothingEnabled = true; c.imageSmoothingQuality = 'high';
        c.drawImage(canvas, 0, 0, w, h);
        return out;
    }

    // ── greyscale ───────────────────────────────────────────────────────────
    /* Rec.709 luma, not the average: a naive (r+g+b)/3 turns a red-lit face
       muddy and lifts a blue backdrop, which on a document photo reads as a
       dirty background. Some offices still ask for black-and-white prints
       (военный билет, older медкнижки), so this is a real output, not a filter. */
    function toGrayscale(canvas) {
        var W = canvas.width, H = canvas.height;
        var out = document.createElement('canvas'); out.width = W; out.height = H;
        var ctx = out.getContext('2d');
        ctx.drawImage(canvas, 0, 0);
        var img = ctx.getImageData(0, 0, W, H), d = img.data;
        for (var i = 0; i < d.length; i += 4) {
            var y = 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
            d[i] = d[i + 1] = d[i + 2] = y;
        }
        ctx.putImageData(img, 0, 0);
        return out;
    }

    // ── the corner («уголок») ───────────────────────────────────────────────
    /**
     * A white right triangle in a bottom corner, legs `mm` long, left blank so
     * the issuing office can stamp its seal across photo and page without the
     * seal landing on the face.
     *
     * Legs are computed from the PRINT size, not from pixels, so 10 mm is
     * 10 mm on a 30×40 at 300 dpi and on a 35×45 at 600 dpi alike. Drawn last,
     * at final size, after any downsample — a triangle drawn big and then
     * resampled arrives with a soft hypotenuse, and this edge is the one thing
     * on the print that a human will line a stamp up against.
     *
     * Returns a NEW canvas: the caller toggles this on and off while looking at
     * it, and a corner painted in place cannot be taken back off.
     */
    function cornerLegsPx(spec, corner) {
        var T = targetPx(spec);
        var mm = Math.max(0, +corner.mm || 0);
        return {
            w: Math.round(mm / spec.print_w_mm * T.w),
            h: Math.round(mm / spec.print_h_mm * T.h),
        };
    }
    function applyCorner(canvas, spec, corner) {
        if (!corner || !corner.on || !(+corner.mm > 0)) return canvas;
        var legs = cornerLegsPx(spec, corner);
        if (legs.w < 1 || legs.h < 1) return canvas;
        var W = canvas.width, H = canvas.height;
        // legs were computed at print size; honour a canvas rendered at k×
        var kx = W / targetPx(spec).w, ky = H / targetPx(spec).h;
        var lw = Math.round(legs.w * kx), lh = Math.round(legs.h * ky);
        var out = document.createElement('canvas'); out.width = W; out.height = H;
        var ctx = out.getContext('2d');
        ctx.drawImage(canvas, 0, 0);
        ctx.fillStyle = corner.color || '#ffffff';
        ctx.beginPath();
        if (corner.side === 'left') {
            ctx.moveTo(0, H); ctx.lineTo(lw, H); ctx.lineTo(0, H - lh);
        } else {
            ctx.moveTo(W, H); ctx.lineTo(W - lw, H); ctx.lineTo(W, H - lh);
        }
        ctx.closePath();
        ctx.fill();
        // Optional hairline on the hypotenuse. Off by default and it must stay
        // that way — the corner is supposed to be blank paper, and a line is a
        // mark on the print. It exists because on a WHITE background a white
        // corner is invisible, and someone printing at home has no other way to
        // check the file carries one at all.
        if (corner.outline) {
            ctx.strokeStyle = 'rgba(0,0,0,.28)';
            ctx.lineWidth = Math.max(1, Math.round(Math.min(W, H) / 500));
            ctx.beginPath();
            if (corner.side === 'left') { ctx.moveTo(lw, H); ctx.lineTo(0, H - lh); }
            else { ctx.moveTo(W - lw, H); ctx.lineTo(W, H - lh); }
            ctx.stroke();
        }
        return out;
    }

    /*
     * Canvas → JPEG goes through the site's one encoder, window.__canvasBlob
     * (timbrica-core.js: toBlob → OffscreenCanvas → data URL, each rung with a
     * deadline; no rung answering is a rejection with code CANVAS_ENCODE_FAILED).
     * The bare toBlob here resolved `null` — WebKit's way of saying it had no
     * memory for the canvas — and `b.size` then killed the checklist and the
     * Download button alike (Sentry TIMBRICA-FRONTEND-QE, Yandex on iOS 27,
     * 18.10.2026).
     *
     * A finished photo is opaque everywhere: render() paints the document's
     * backdrop before the crop. A transparent or unreadable pixel therefore means
     * a canvas WebKit never gave a buffer to — drawing into it went nowhere, and
     * the OffscreenCanvas rung would faithfully encode that nothing as a black
     * JPEG. Such a canvas is not encoded; the caller hears why instead.
     */
    function encodeRefused(why) {
        var e = new Error('passport photo could not be encoded (' + why + ')');
        e.name = 'CanvasEncodeError'; e.code = 'CANVAS_ENCODE_FAILED'; e.phase = 'encode'; e.why = why;
        return e;
    }
    function hasPixels(canvas) {
        try { return canvas.getContext('2d').getImageData(0, 0, 1, 1).data[3] > 0; }
        catch (_) { return false; }
    }
    function blobAt(canvas, q) {
        if (!hasPixels(canvas)) return Promise.reject(encodeRefused('no_buffer'));
        return window.__canvasBlob(canvas, 'image/jpeg', q);
    }

    // Descending-quality search to land inside [min,max] KB; downscale if max is
    // unreachable even at q=0.30 (for hard gates like DV ≤240KB); never hangs.
    async function exportJpegSizeBand(canvas, spec) {
        var hi = spec.filesize_max_kb || Infinity, lo = spec.filesize_min_kb || 0;
        if (hi === Infinity && lo === 0) {
            var b0 = await blobAt(canvas, 0.92);
            return { blob: b0, sizeKB: +(b0.size / 1024).toFixed(1), quality: 0.92, width: canvas.width, height: canvas.height, warned: false, downscaled: false };
        }
        var work = canvas, downscaled = false, attempt = 0;
        while (attempt < 4) {
            var q = 0.95, best = null;
            for (var i = 0; i < 11; i++) {
                var b = await blobAt(work, q), kb = b.size / 1024;
                best = { blob: b, kb: kb, q: q };
                if (kb <= hi) break;
                q -= 0.07; if (q < 0.30) break;
            }
            if (best.kb <= hi || work.width <= 320) {
                return { blob: best.blob, sizeKB: +best.kb.toFixed(1), quality: +best.q.toFixed(2),
                         width: work.width, height: work.height, warned: best.kb > hi || best.kb < lo, downscaled: downscaled };
            }
            // still over the hard cap at min quality → downscale 15% and retry
            var nw = Math.round(work.width * 0.85), nh = Math.round(work.height * 0.85);
            var dn = document.createElement('canvas'); dn.width = nw; dn.height = nh;
            var c = dn.getContext('2d');
            if (!c) throw encodeRefused('no_buffer');
            c.imageSmoothingQuality = 'high'; c.drawImage(work, 0, 0, nw, nh);
            // the step it replaces is ours, and WebKit gives canvas memory back only at 0×0
            if (work !== canvas) { work.width = work.height = 0; }
            work = dn; downscaled = true; attempt++;
        }
        var fb = await blobAt(work, 0.6);
        return { blob: fb, sizeKB: +(fb.size / 1024).toFixed(1), quality: 0.6, width: work.width, height: work.height, warned: true, downscaled: downscaled };
    }

    // ── compliance checklist (computed from the same geometry; honest, never "guaranteed") ──
    function invMapper(tr) {
        var m = tr.matrix, det = m[0] * m[3] - m[1] * m[2];
        return function (x, y) {
            var ia = m[3] / det, ib = -m[1] / det, ic = -m[2] / det, id = m[0] / det,
                ie = (m[2] * m[5] - m[3] * m[4]) / det, iff = (m[1] * m[4] - m[0] * m[5]) / det;
            return { x: ia * x + ic * y + ie, y: ib * x + id * y + iff };
        };
    }
    function coverage(tr, W, H) {
        var inv = invMapper(tr), pts = [inv(0, 0), inv(tr.TW, 0), inv(tr.TW, tr.TH), inv(0, tr.TH)];
        return pts.every(function (p) { return p.x >= -1 && p.y >= -1 && p.x <= W + 1 && p.y <= H + 1; });
    }
    /**
     * Background uniformity, sampled at the two TOP corners — the only part of
     * a head-and-shoulders frame that is backdrop by construction.
     *
     * The corner («уголок») is at the BOTTOM, so it can never be sampled here;
     * that is deliberate, not luck. A white triangle read as "background" would
     * make every cornered photo report a perfectly uniform backdrop.
     */
    /**
     * How even the background is — measured over the whole visible wall.
     *
     * It used to be two patches in the two TOP corners, and that is the shape of
     * the miss: a cast shadow in a home photo lands beside the head and below
     * it, exactly where neither patch looks. Measured over a corpus of passport
     * crops with cast shadows generated as the subject's own projected
     * silhouette, five of five shadowed backgrounds were reported "pass" — one
     * of them 84 levels darker on the shadow side than on the lit side — and the
     * verdict stayed "pass" even after a replacement pass had DOUBLED the
     * shadow's contrast. A document office rejects exactly that photo, so the
     * check was reassuring people out of the one thing it existed to catch.
     *
     * `drop` is the honest quantity: the spread between the bright part of the
     * wall and its dark part, both taken from the wall itself. The two-patch
     * reading survives only as the fallback for a frame where no wall can be
     * found at all (a head filling the crop edge to edge).
     */
    function sampleUniformity(canvas) {
        try {
            if (window.PassportWallField) {
                var A = window.PassportWallField.analyse(canvas);
                if (A && A.coverage > 0.04) {
                    return { ok: true, mode: 'field', drop: A.drop, lit: A.lit, coverage: A.coverage };
                }
            }
        } catch (e) { /* fall through to the corner reading */ }
        try {
            var ctx = canvas.getContext('2d'), W = canvas.width, H = canvas.height, s = Math.max(8, Math.round(Math.min(W, H) * 0.08));
            function patch(x, y) {
                var d = ctx.getImageData(x, y, s, s).data, n = d.length / 4, mr = 0, mg = 0, mb = 0, i;
                for (i = 0; i < d.length; i += 4) { mr += d[i]; mg += d[i + 1]; mb += d[i + 2]; }
                mr /= n; mg /= n; mb /= n;
                var v = 0; for (i = 0; i < d.length; i += 4) { v += (d[i] - mr) * (d[i] - mr) + (d[i + 1] - mg) * (d[i + 1] - mg) + (d[i + 2] - mb) * (d[i + 2] - mb); }
                return { r: mr, g: mg, b: mb, std: Math.sqrt(v / (3 * n)) };
            }
            var tl = patch(0, 0), tr = patch(W - s, 0);
            var diff = Math.sqrt((tl.r - tr.r) * (tl.r - tr.r) + (tl.g - tr.g) * (tl.g - tr.g) + (tl.b - tr.b) * (tl.b - tr.b));
            return { ok: true, mode: 'corners', std: Math.max(tl.std, tr.std), diff: diff };
        } catch (e) { return { ok: false, mode: 'none' }; }
    }

    /**
     * Shadow ON the face — the half of the complaint no background lane can
     * touch. A ceiling lamp to one side leaves one cheek lit and the other in
     * shade, and every document that asks for an even background asks for an
     * evenly lit face in the same sentence.
     *
     * Read straight off the mesh the crop is already built from: two patches on
     * the cheeks, mirrored about the eye line, compared as a RATIO so it does
     * not fire on a dark-skinned or a pale subject. `geom` is in SOURCE pixels,
     * so it travels through the same matrix the render used.
     */
    function faceEvenness(canvas, geom, tr) {
        try {
            var m = tr.matrix, k = canvas.width / tr.TW;
            var to = function (p) {
                return { x: (m[0] * p.x + m[2] * p.y + m[4]) * k, y: (m[1] * p.x + m[3] * p.y + m[5]) * k };
            };
            var L = to(geom.cheekL), R = to(geom.cheekR), chin = to(geom.chin);
            var eye = to({ x: geom.eyeX, y: geom.eyeY });
            var r = Math.max(6, Math.round(Math.hypot(R.x - L.x, R.y - L.y) * 0.11));
            // Halfway from the eye line to the chin: past the brow, above the
            // mouth — the flattest part of a cheek at any expression.
            var midY = (eye.y + chin.y) / 2;
            var ctx = canvas.getContext('2d', { willReadFrequently: true });
            function mean(cx, cy) {
                var x0 = Math.max(0, Math.min(canvas.width - 2 * r, Math.round(cx - r)));
                var y0 = Math.max(0, Math.min(canvas.height - 2 * r, Math.round(cy - r)));
                var d = ctx.getImageData(x0, y0, 2 * r, 2 * r).data, s = 0, n = d.length / 4;
                for (var i = 0; i < d.length; i += 4) s += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
                return s / n;
            }
            // Inset from the face edge so the patch is cheek, not the boundary.
            var a = mean(L.x + (R.x - L.x) * 0.20, midY);
            var b = mean(L.x + (R.x - L.x) * 0.80, midY);
            var hi = Math.max(a, b), lo = Math.min(a, b);
            if (hi < 12) return { ok: false };
            return { ok: true, ratio: lo / hi, diff: hi - lo };
        } catch (e) { return { ok: false }; }
    }
    // Returns language-NEUTRAL rows {id, status, detail}. The UI layer maps id →
    // a localized label; detail is numbers/units only (no translatable words).
    function evaluateCompliance(a, tr, spec, rendered, exp, deTilt, extra) {
        extra = extra || {};
        var MM = extra.mm || 'mm';   // «мм» / «مم» — the caller owns the reader's unit
        var rows = [], g = a.geom;
        var add = function (id, status, detail) { rows.push({ id: id, status: status, detail: detail || '' }); };
        add('single_face', a.faces === 1 ? 'pass' : 'fail', a.faces === 1 ? '' : '×' + a.faces);
        var turn = g.faceWidth ? Math.abs(((g.eyeX - g.cheekL.x) - (g.cheekR.x - g.eyeX)) / g.faceWidth) : 0;
        add('frontal', turn < 0.12 ? 'pass' : (turn < 0.2 ? 'warn' : 'fail'), (turn * 100).toFixed(0) + '%');
        var outRoll = deTilt ? 0 : g.rollDeg;
        add('level', Math.abs(outRoll) <= 2 ? 'pass' : (Math.abs(outRoll) <= 5 ? 'warn' : 'fail'), deTilt ? '' : (g.rollDeg.toFixed(1) + '°'));
        // Head size is stated in the unit the document itself uses: a Russian
        // office measures millimetres of head, an ICAO note quotes a percentage.
        var hpct = tr.headFrac * 100, hmin = +spec.head_pct_min || 0, hmax = +spec.head_pct_max || 100;
        var hDetail = (spec.head_mode === 'ru_inverted' && spec.print_h_mm)
            ? (tr.headFrac * spec.print_h_mm).toFixed(1) + ' ' + (spec.head_mm_min ? '/ ' + spec.head_mm_min + '–' + spec.head_mm_max + ' ' + MM : MM)
            : hpct.toFixed(0) + '%';
        add('head_size', (hpct >= hmin - 3 && hpct <= hmax + 3) ? 'pass' : 'warn', hDetail);
        add('centred', 'pass', '');
        add('coverage', coverage(tr, a.W, a.H) ? 'pass' : 'warn', '');
        /* Boundaries measured, not chosen: on a corpus of passport crops the
           wall's own drop reads 0 when it is flat, 10 for a falloff you have to
           look for, 22 for one you notice, and 43…109 for every real cast
           shadow. Relative to the lit level, so a wall that is painted grey is
           not judged by the same absolute levels as a white one. */
        var u = sampleUniformity(rendered);
        if (u.ok && u.mode === 'field') {
            var rel = u.lit > 8 ? u.drop / u.lit : 0;
            add('background', rel <= 0.08 ? 'pass' : (rel <= 0.17 ? 'warn' : 'fail'), Math.round(u.drop) + '/' + Math.round(u.lit));
        } else if (u.ok) {
            add('background', (u.std < 22 && u.diff < 30) ? 'pass' : ((u.std < 45 && u.diff < 55) ? 'warn' : 'fail'), u.std.toFixed(0));
        }
        /* Shadow ON the face. Every document that asks for an even background
           asks for an evenly lit face in the same sentence, and nothing here
           used to look at the face at all.
           ⚠ These boundaries are DELIBERATELY slack, and the reason is a limit
           of the evidence rather than of the idea. Calibrated on one portrait
           with shadows added at known strengths, the reading goes: 21% of
           left-right difference on the clean photo (ordinary modelling, no
           shadow at all), 30% for a broad falloff that is still just lighting,
           then 40 / 47 / 56% as a real side shadow deepens. What is NOT known
           from one face is how wide 21% varies between people — so the row is
           set to stay silent through everything the control reaches and to
           speak only where the corpus says a shadow certainly is. A checklist
           that cries on ordinary portraits is a checklist people stop reading,
           which is the same disease as the verdict this change came to fix.
           Narrowing it wants a corpus of real document photos we do not keep. */
        var fe = faceEvenness(rendered, g, tr);
        if (fe.ok) add('face_light', fe.ratio >= 0.66 ? 'pass' : (fe.ratio >= 0.56 ? 'warn' : 'fail'),
            fe.ratio >= 0.66 ? '' : ('−' + Math.round((1 - fe.ratio) * 100) + '%'));
        if (a.openness) {
            // Thresholds are aspect ratios, not blendshape scores: a fully open
            // eye measures ≈0.28–0.38 lid gap over eye width, a blink ≈0.05.
            var e = a.openness.ear;
            add('eyes_open', e >= 0.19 ? 'pass' : 'warn', e < 0.19 ? e.toFixed(2) : '');
            var m = a.openness.mar;
            add('mouth_closed', m <= 0.12 ? 'pass' : 'warn', m > 0.12 ? m.toFixed(2) : '');
        }
        add('resolution', tr.s <= 1.6 ? 'pass' : 'warn', tr.s > 1.6 ? ('×' + tr.s.toFixed(1)) : '');
        if (spec.filesize_max_kb && exp) add('filesize', exp.sizeKB <= spec.filesize_max_kb ? 'pass' : 'fail', exp.sizeKB + ' / ' + spec.filesize_max_kb + ' KB');
        if (extra.corner && extra.corner.on) add('corner', 'info', extra.corner.mm + ' ' + MM + ' · ' + (extra.corner.side === 'left' ? '◣' : '◢'));
        if (extra.gray) add('grayscale', 'info', '');
        if (spec.glasses_allowed === false) add('glasses', 'info', '');
        if (spec.allow_ai_retouch === false) add('no_ai', 'info', '');
        return rows;
    }

    // ── background replacement ──────────────────────────────────────────────
    function hexToRgb(hex) {
        var h = (hex || '#ffffff').replace('#', '');
        if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
        return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
    }

    var _matte = null;
    /**
     * Which ONNX Runtime build the browser gets is NOT ours to decide: on
     * WebKit the JSEP bundle walks the tab from 1 GB to 14 GB and gets it
     * killed (onnxruntime#26827), and the answer already has an owner —
     * `window.__ortBuild` in device-capabilities.js. That string was re-decided,
     * identically and wrongly, in 36 files before the owner existed; a
     * fail-safe literal here would make this the 37th. So the lane refuses
     * instead: no owner, no cut-out, and the reason lands in telemetry.
     */
    function matting() {
        if (_matte) return _matte;
        if (!window.BgMatting || !CFG.matting) throw new Error('matting-unavailable');
        var ob = window.__ortBuild;
        if (!ob || !ob.url) throw new Error('ort-build-owner-missing');
        var urls = Object.assign({}, CFG.matting, { ort: ob.url(), wasmBase: ob.wasmBase() });
        // The wasm-only bundle has no WebGPU execution provider — a caller that
        // still asks for one pays a failed createSession per session.
        if (ob.wasmOnly && ob.wasmOnly() && urls.models && urls.models.main) {
            urls.models = Object.assign({}, urls.models, {
                main: Object.assign({}, urls.models.main, { webgpu: false })
            });
        }
        _matte = window.BgMatting.create({ slug: 'passport-photo', urls: urls });
        return _matte;
    }
    function mattingCached() {
        try { return matting().isCached('main'); } catch (_) { return Promise.resolve(false); }
    }

    /**
     * `chroma` finds the wall as a region and models its lighting (wall-field.js);
     * `ai` asks the shared matting engine (the same isnet weights /bg-remover
     * uses, so a visitor who has been there pays nothing) for a real alpha matte.
     *
     * Measured against each other on passport crops with ground-truth alpha,
     * the two now differ far less than the wait between them: shadow left behind
     * 2.9–5.6 levels for the wall lane against 0.9–2.8 for the matte, subject
     * damage 0.0% against 0.1%, and 80–250 ms against 16–23 s plus an 86 MB
     * download. So the wall lane is the one that should be reached for first,
     * and it is the ONLY one available on documents that forbid AI retouch.
     *
     * The AI lane is worth its download for exactly one reason: hair. A binary
     * person/not-person mask cuts hair off at the silhouette, which against a
     * white document background is the single most obvious sign a photo was
     * edited. `estimateForeground` also un-mixes the old backdrop colour out of
     * the semi-transparent edge, so a subject photographed against a green wall
     * does not arrive with a green rim.
     */
    async function replaceBackground(canvas, mode, spec, opts) {
        if (mode === 'none' || !mode) return canvas;
        opts = opts || {};
        var W = canvas.width, H = canvas.height, bg = hexToRgb(opts.bgHex || spec.bg_hex);

        if (mode === 'ai') {
            var eng = matting();
            await eng.prepare('main', opts.onProgress, opts.signal);
            var r = await eng.cut(canvas, { maxSide: Math.max(512, Math.min(1536, Math.max(W, H))), refine: true, onStage: opts.onStage });
            var out = document.createElement('canvas'); out.width = W; out.height = H;
            var octx = out.getContext('2d');
            var img = octx.createImageData(r.w, r.h), d = img.data;
            var src = canvas.getContext('2d').getImageData(0, 0, W, H).data;
            var sx = W / r.w, sy = H / r.h;
            for (var y = 0; y < r.h; y++) {
                for (var x = 0; x < r.w; x++) {
                    var i = y * r.w + x, i4 = i * 4, a = r.alpha[i];
                    var fr, fg2, fb;
                    if (r.fg) { fr = r.fg[i * 3] * 255; fg2 = r.fg[i * 3 + 1] * 255; fb = r.fg[i * 3 + 2] * 255; }
                    else {
                        var s4 = (((y * sy) | 0) * W + ((x * sx) | 0)) * 4;
                        fr = src[s4]; fg2 = src[s4 + 1]; fb = src[s4 + 2];
                    }
                    d[i4] = fr * a + bg.r * (1 - a);
                    d[i4 + 1] = fg2 * a + bg.g * (1 - a);
                    d[i4 + 2] = fb * a + bg.b * (1 - a);
                    d[i4 + 3] = 255;
                }
            }
            if (r.w === W && r.h === H) { octx.putImageData(img, 0, 0); return out; }
            var tmp = document.createElement('canvas'); tmp.width = r.w; tmp.height = r.h;
            tmp.getContext('2d').putImageData(img, 0, 0);
            octx.imageSmoothingQuality = 'high';
            octx.drawImage(tmp, 0, 0, W, H);
            return out;
        }

        /* `chroma`: the wall is found as a REGION and modelled with its own
           illumination field, then painted over. It used to be a distance from
           the mean of the two top corners, and that model has no way to hold a
           shadow: measured on passport crops with real cast shadows, the
           shadowed wall sat 112–120 from that mean — past the slider's whole
           reach of 130 — while the brightest SKIN sat 43–49, i.e. NEARER. Skin
           lay between the wall and the wall's own shadow, so the mode left the
           shadow (making it worse: it whitened the lit side and doubled the
           contrast, 59 levels of drop becoming 102) and carved white holes out
           of the lit half of the face, 9.3% of the subject's pixels moved on a
           FLAT wall. Both numbers are 0.0 now. See wall-field.js. */
        if (!window.PassportWallField) throw new Error('wall-field-unavailable');
        var res = window.PassportWallField.key(canvas, bg, opts.wall);
        if (!res) throw new Error('wall-not-found');
        // How much of the frame turned out to BE wall is the one thing the
        // reader may need to act on (a poster or a curtain behind them stops
        // the walk), so it is reported rather than left as a silent shrug.
        if (opts.onWall) { try { opts.onWall(res.analysis); } catch (_) { } }
        return res.canvas;
    }

    window.PassportPhotoEngine = {
        IDX: IDX,
        configure: configure, init: init, warm: warm,
        ready: function () { return !!(window.FRLandmarks && window.FRLandmarks.loaded && window.FRLandmarks.loaded()); },
        analyze: analyze, analyzeAt: analyzeAt, estimateCrownY: estimateCrownY,
        targetPx: targetPx, headFraction: headFraction, crownFraction: crownFraction,
        computeTransform: computeTransform, render: render, resample: resample, mattingScale: mattingScale,
        applyCorner: applyCorner, cornerLegsPx: cornerLegsPx, toGrayscale: toGrayscale,
        exportJpegSizeBand: exportJpegSizeBand,
        evaluateCompliance: evaluateCompliance, coverage: coverage,
        replaceBackground: replaceBackground, mattingCached: mattingCached, hexToRgb: hexToRgb
    };
})();
